import { Flex, FormControl, FormLabel, Switch } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import SubmitButton from 'components/ui/SubmitButton';
import { useSuccessDialogContext } from 'components/ui/SuccessDialog/SuccessDialogContext';
import SimpleEditor from 'features/editor/components/SimpleEditor/SimpleEditor';
import { useMessages } from 'hooks/useMessages';
import { preventEnterKeySubmission } from 'lib/validation';
import { FormProvider, Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import NoteTopic from 'types/entities/note-topic.entity';
import * as Yup from 'yup';
import { useAgendaContext } from '../contexts/AgendaContext';
import { Inputs, useSaveNoteTopic } from '../hooks/useSaveNoteTopic';

interface FormNoteTopicProps {
  topic?: NoteTopic
}

const validationSchema = Yup.object().shape({
  noteId: Yup.string().required(),
  text: Yup.string().required(),
  highlighted: Yup.boolean().required()
});

export default function FormNoteTopic({ topic }: FormNoteTopicProps) {
  const { modals } = useAgendaContext();
  const { openSuccessDialog } = useSuccessDialogContext();
  const { id } = useParams() as { id: string };
  const defaultValues: Inputs = {
    noteId: id,
    text: topic?.text || '',
    highlighted: !!topic?.highlighted
  };

  const methods = useForm<Inputs>({
    mode: 'all',
    resolver: yupResolver(validationSchema) as unknown as Resolver<Inputs>,
    defaultValues
  });

  const { register, control, handleSubmit, reset, formState: { errors, isValid } } = methods;

  const { onError } = useMessages();

  const { 
    onSaveNoteTopic, 
    isPending,
  } = useSaveNoteTopic({ 
        topicId: topic?.id,
        onSuccess: () => {
          reset();
          modals.noteTopic.onClose();

          if (!topic) {
            openSuccessDialog({
              title: 'Novo tópico criado!',
              description: `Texto explicativo`,
              actionButtonText: 'Criar outro tópico',
              actionButton: modals.noteTopic.onOpen,
              closeButtonText: 'Tudo certo'
            });
          }
        },
        onError: (error) => onError(error?.response?.data?.message)
      });
    
  const onSubmit: SubmitHandler<Inputs> = (data) => onSaveNoteTopic(data);
  
  return (
    <FormProvider {...methods}>
      <form 
        onSubmit={handleSubmit(onSubmit)}
        onKeyPress={preventEnterKeySubmission}
      >
        <FormControl isInvalid={!!errors.text} isRequired>
          <SimpleEditor 
            name="text"
            control={control}
            skipExtensionConfigs
            options={{
              autofocus: true
            }}
          />
        </FormControl>
        <FormControl
          display="flex" 
          alignItems="center" 
          gap="10px"
          pt="22px"
          w="auto"
          isInvalid={!!errors.highlighted}
        >
          <Switch 
            id="highlighted" 
            {...register('highlighted')}
          />
          <FormLabel htmlFor="highlighted" mb="0" whiteSpace="nowrap">
            Exibir como destaque
          </FormLabel>
        </FormControl>

        <Flex>
          <SubmitButton 
            isDisabled={isPending || !isValid}
            isLoading={isPending}
            alignSelf="flex-end"
            ml="auto"
            hideIcon
          >
            { topic ? 'Alterar' : 'Adicionar' }
          </SubmitButton>
        </Flex>
      </form>
    </FormProvider>
  )
}
